import React, {useMemo} from 'react'
import {useLocation} from 'react-router-dom'
import loadable from '@loadable/component'
import {HTTPError} from '@salesforce/pwa-kit-react-sdk/ssr/universal/errors'
import {useUrlMapping} from '@salesforce/commerce-sdk-react'
import {removeSiteLocaleFromPath} from '../../utils/url'
import LoadingSpinner from '../../components/loading-spinner'
import CategoryRouting from './category-routing'

const PageNotFound = loadable(() => import('../page-not-found'))

/**
 * UrlMappedRouting component that routes to different components based on URL mappings.
 *
 * Currently supports routing to:
 * - Category pages (renders CategoryRouting component)
 * - Page Not Found for unmapped URLs
 *
 * Note: it currently statically links to CategoryRouting because `loadable` breaks preloading of data on SSR when used outside route components.
 *
 * @param {object} params - Props to pass to the routed component.
 * @returns {Element}
 * @constructor
 */
const UrlMappedRouting = (params) => {
    const location = useLocation()

    const urlSegment = useMemo(
        () => removeSiteLocaleFromPath(location.pathname),
        [location.pathname]
    )
    const {
        error: urlMappingError,
        data: urlMapping,
        isLoading
    } = useUrlMapping(
        {
            parameters: {
                urlSegment
            }
        },
        {}
    )

    const urlMappingErrorStatus = urlMappingError?.response?.status
    switch (urlMappingErrorStatus) {
        case undefined:
            // No Error.
            break
        case 404:
            return <PageNotFound {...params} />
        default:
            throw new HTTPError(
                urlMappingErrorStatus,
                `HTTP Error ${urlMappingErrorStatus} occurred.`
            )
    }

    if (isLoading) {
        return <LoadingSpinner />
    }

    switch (urlMapping.resourceType) {
        case 'category':
            return <CategoryRouting {...params} categoryId={urlMapping.resourceId} />
        default:
            return <PageNotFound {...params} />
    }
}

export default UrlMappedRouting
